/**
 * Admin domain types and Zod schemas
 *
 * Story 7.1: Admin Token Verification and Warning API
 * Story 7.2: Platform Statistics API
 */

import { z } from "zod";

import { TokenResponseSchema, TokenSchema, VerificationStatusSchema } from "./token";

// ─── Verify Token Request ────────────────────────────────────
export const VerifyTokenRequestSchema = z
	.object({
		status: VerificationStatusSchema,
		reason: z.string().trim().max(500).optional(),
	})
	.refine(
		(data) => data.status !== "flagged" || (data.reason !== undefined && data.reason.length > 0),
		{
			message: "Reason is required when flagging a token",
			path: ["reason"],
		}
	);

export type VerifyTokenRequest = z.infer<typeof VerifyTokenRequestSchema>;

// ─── Verify Token Response ───────────────────────────────────
export const VerifyTokenResponseSchema = z.object({
	token: TokenSchema,
	previousStatus: VerificationStatusSchema,
});

export type VerifyTokenResponse = z.infer<typeof VerifyTokenResponseSchema>;

// ─── Admin Stats Response (Story 7.2) ───────────────────────
export const AdminStatsResponseSchema = z.object({
	tokensLaunched: z.number(),
	totalVolume: z.string().nullable(),
	activeWallets24h: z.number().nullable(),
	verificationBreakdown: z.object({
		unverified: z.number(),
		verified: z.number(),
		flagged: z.number(),
		removed: z.number(),
	}),
	recentTokens: z.array(TokenResponseSchema).max(10),
	dataSource: z.enum(["d1", "cache", "d1+indexer"]),
	dataAge: z.number(),
});

export type AdminStatsResponse = z.infer<typeof AdminStatsResponseSchema>;

// ─── List Admin Tokens Query Schema (Story 7.2) ─────────────
export const ListAdminTokensQuerySchema = z.object({
	search: z.string().max(200).optional(),
	verification: VerificationStatusSchema.optional(),
	cursor: z.string().optional(),
	limit: z.coerce.number().int().min(1).max(100).default(20),
});

export type ListAdminTokensQueryParams = z.infer<typeof ListAdminTokensQuerySchema>;
